/*
 * Cart urgency timer. The start time is stored per cart (its token) in localStorage, so the count
 * keeps going across pages, reloads and drawer re-renders. At zero it only swaps the message:
 * it never reloads the page or touches the cart.
 *
 * Liquid does not expose cart.token on the storefront, so the token is read from /cart.js. The saved
 * start is dropped when the cart is empty, so a new cart (after checkout or after removing every
 * item) always starts a fresh countdown.
 */
(() => {
  const KEY = 'eva:cart-timer';
  const clearSaved = () => {
    try {
      localStorage.removeItem(KEY);
    } catch (error) {
      // Storage blocked: nothing to clear.
    }
  };

  window.evaCartTimer = {
    key: KEY,
    clear: clearSaved,
    cart: () =>
      fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } }).then((response) =>
        response.json()
      ),
  };

  // A cart emptied on a previous page: forget its countdown.
  try {
    if (localStorage.getItem(KEY)) {
      window.evaCartTimer
        .cart()
        .then((cart) => {
          if (!cart.item_count) clearSaved();
        })
        .catch(() => {});
    }
  } catch (error) {
    // Storage blocked.
  }

  // A cart emptied on this page (last item removed).
  const watchCart = () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return false;
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.cartData && event.cartData.item_count === 0) clearSaved();
    });
    return true;
  };
  if (!watchCart()) document.addEventListener('DOMContentLoaded', watchCart);
})();

if (!customElements.get('cart-timer')) {
  customElements.define(
    'cart-timer',
    class CartTimer extends HTMLElement {
      connectedCallback() {
        this.duration = (Number(this.dataset.minutes) || 10) * 60 * 1000;
        // Until the token is known, show the saved countdown (or a fresh one) without saving anything.
        this.start = this.dataset.cartToken ? this.readStart(this.dataset.cartToken) : this.peekStart();
        this.tick();
        this.interval = setInterval(() => this.tick(), 1000);

        if (!this.dataset.cartToken) {
          window.evaCartTimer
            .cart()
            .then((cart) => {
              if (!this.isConnected) return;
              this.dataset.cartToken = cart.token || '';
              this.start = this.readStart(this.dataset.cartToken);
              this.tick();
            })
            .catch(() => {});
        }
      }

      disconnectedCallback() {
        clearInterval(this.interval);
      }

      peekStart() {
        try {
          const saved = JSON.parse(localStorage.getItem(window.evaCartTimer.key) || 'null');
          if (saved && Number(saved.start) > 0) return Number(saved.start);
        } catch (error) {
          // Storage blocked.
        }
        return Date.now();
      }

      readStart(token) {
        const key = window.evaCartTimer.key;
        try {
          const saved = JSON.parse(localStorage.getItem(key) || 'null');
          if (saved && saved.token === token && Number(saved.start) > 0) return Number(saved.start);
          const start = Date.now();
          localStorage.setItem(key, JSON.stringify({ token, start }));
          return start;
        } catch (error) {
          // Storage blocked: count from now, for this page only.
          return Date.now();
        }
      }

      tick() {
        const left = Math.max(0, this.start + this.duration - Date.now());
        const expired = left === 0;
        const running = this.querySelector('.cart-timer__running');
        const done = this.querySelector('.cart-timer__expired');
        if (running) running.hidden = expired;
        if (done) done.hidden = !expired;
        this.classList.toggle('is-expired', expired);

        if (!expired) {
          const seconds = Math.ceil(left / 1000);
          const text = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
          const time = this.querySelector('[data-cart-timer-time]');
          if (time && time.textContent !== text) time.textContent = text;
        } else {
          clearInterval(this.interval);
        }
      }
    }
  );
}
